"use client";

import { useLayoutEffect, useRef, useState } from "react";

import { cn } from "@/lib/utils";

type Props = {
	/** Plain text title (service listing cards). */
	text: string;
	/** Wrapper classes (e.g. min height for carousel alignment). */
	className?: string;
	/** Classes on the clamped text node. */
	textClassName?: string;
	/** Bottom fade when clamped (defaults to `h-7`). */
	fadeClassName?: string;
};

/**
 * Up to three lines; when text overflows, a short bottom gradient hints there is more.
 */
export function ServiceCardTitleClamp({
	text,
	className,
	textClassName,
	fadeClassName,
}: Props) {
	const ref = useRef<HTMLSpanElement>(null);
	const [overflow, setOverflow] = useState(false);

	useLayoutEffect(() => {
		const el = ref.current;
		if (!el) return;
		const measure = () => {
			setOverflow(el.scrollHeight > el.clientHeight + 1);
		};
		measure();
		const ro = new ResizeObserver(measure);
		ro.observe(el);
		return () => ro.disconnect();
	}, [text]);

	return (
		<div
			className={cn(
				// Keep font size in sync with card `3lh` height budgets (`text-sm leading-snug`).
				// Do not use `block` here — it overrides line-clamp's `-webkit-box` display.
				"relative min-h-[3lh] max-h-[3lh] min-w-0 overflow-hidden text-sm leading-snug",
				className,
			)}
		>
			<span
				ref={ref}
				className={cn(
					"font-medium text-foreground line-clamp-3 [overflow-wrap:anywhere]",
					textClassName,
				)}
			>
				{text}
			</span>
			<div
				aria-hidden
				className={cn(
					"pointer-events-none absolute inset-x-0 bottom-0 z-[1] h-7 bg-gradient-to-t from-card via-card/80 to-transparent transition-opacity duration-200 dark:from-card dark:via-card/80",
					fadeClassName,
					overflow ? "opacity-100" : "opacity-0",
				)}
			/>
		</div>
	);
}
